import { UseSortModelActionsReturn } from '../hooks/useSortModelActions';
import { SortDirection, getSortPriority } from '../sortModel';

export type SortMenuLabelKey = 'sort_by' | 'sort_ascending' | 'sort_descending';

export interface SortMenuItem {
  isSorted: boolean;
  /** Current direction, when the column is part of the sort model. */
  direction?: SortDirection;
  /** One-based sort position, only once more than one column is sorted. */
  priority?: number;
  labelKey: SortMenuLabelKey;
  /** True when the column is unsorted and the sort level cap is reached. */
  disabled: boolean;
  /** Appends the column as a new level, or reverses it when already sorted. */
  apply: () => void;
  /** Drops this column's level. Undefined when the column is not sorted. */
  remove?: () => void;
}

/**
 * Resolves what the context menu should offer for one column. Every action edits
 * a single sort level, so neither surface can collapse an existing multi-sort.
 *
 * A plain function rather than a hook: the column-picker submenu builds one of
 * these per row inside a map.
 */
export const buildSortMenuItem = (
  sortActions: UseSortModelActionsReturn,
  field: string,
): SortMenuItem => {
  const { sortModel, cycle, setDirection, remove, canAddLevel } = sortActions;
  const direction = sortModel.find((item) => item.field === field)?.sort ?? undefined;

  if (!direction) {
    return {
      isSorted: false,
      labelKey: 'sort_by',
      disabled: !canAddLevel,
      apply: () => cycle(field, { append: true }),
    };
  }

  const reversed: SortDirection = direction === 'asc' ? 'desc' : 'asc';

  return {
    isSorted: true,
    direction,
    priority: getSortPriority(sortModel, field),
    labelKey: reversed === 'desc' ? 'sort_descending' : 'sort_ascending',
    disabled: false,
    apply: () => setDirection(field, reversed),
    remove: () => remove(field),
  };
};
